import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { severityIcon, severityLabel, severityTone } from '@/components/hearth'
import { cn } from '@/lib/utils'
import type { Alert } from '@/types'

interface AlertsListProps {
  alerts: Alert[]
  isLoading?: boolean
  maxItems?: number
}

function AlertRow({ alert }: { alert: Alert }) {
  const tone = severityTone[alert.severity]
  const Icon = severityIcon[alert.severity]

  return (
    <Link
      to={`/alerts?id=${alert.id}`}
      className="hearth-card hearth-press flex items-center gap-3 p-4"
    >
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
          tone.chipBg
        )}
      >
        <Icon className={cn('h-4 w-4', tone.fg)} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-gray-900">{alert.title}</span>
        <span className="mt-0.5 block text-[13px] text-gray-500">
          {severityLabel[alert.severity]} ·{' '}
          {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
        </span>
      </span>
      <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
    </Link>
  )
}

function AlertRowSkeleton() {
  return (
    <div className="hearth-card flex animate-pulse items-center gap-3 p-4">
      <div className="h-9 w-9 rounded-full bg-gray-200" />
      <div className="flex-1">
        <div className="h-4 w-3/4 rounded bg-gray-200" />
        <div className="mt-2 h-3 w-1/2 rounded bg-gray-200" />
      </div>
    </div>
  )
}

/**
 * Compact "Recent alerts" rows for the caregiver home: severity chip,
 * title, human severity language, and relative time. Each row is a
 * tappable card into the alert detail.
 */
export function AlertsList({ alerts, isLoading, maxItems = 3 }: AlertsListProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 2 }).map((_, i) => (
          <AlertRowSkeleton key={i} />
        ))}
      </div>
    )
  }

  const displayedAlerts = alerts.slice(0, maxItems)
  if (displayedAlerts.length === 0) {
    return null
  }

  return (
    <div className="space-y-3">
      {displayedAlerts.map((alert) => (
        <AlertRow key={alert.id} alert={alert} />
      ))}
    </div>
  )
}
